import CommonLookupDisplay from "components/CommonLookupDisplay";
import { GlobalContext } from "components/GlobalContext";
import { IColumnDefinition } from "components/SortableTable";
import IContact, { IContactCreateOrUpdate, IContactFilters, IContactInitResult } from "models/sql/IContact";
import { useContext, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import ContactService from "services/ContactService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faComments, faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";
import { formatDate, formatDateTime } from "utilities/StringUtility";
import { addNewLogEntry } from "utilities/MiscUtilities";

interface IContactsLookupProps {
    baseData?: IContactInitResult;
}

const ContactsLookup = (props: IContactsLookupProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const navigate = useNavigate();
    const [filter, setFilter] = useState<IContactFilters>({});
    const [forceRefresh, setForceRefresh] = useState(new Date());

    const markContactedAsync = async (contact: IContact) => {
        if (window.confirm(`Mark ${contact.firstName} ${contact.lastName} as contacted just now?`)) {
            if (contact) {
                globalContext.setLoading(true);
                const accessToken = await globalContext.getAccessTokenAsync();

                let data: IContactCreateOrUpdate = {
                    contactId: contact.contactId,
                    firstName: contact.firstName,
                    lastName: contact.lastName,
                    email: contact.email,
                    phone: contact.phone,
                    role: contact.role,
                    created: contact.created,
                    notes: contact.notes,
                    systemDataJson: contact.systemDataJson,
                    lastContactDate: (new Date()).toISOString(),
                    lastUpdated: contact.lastUpdated,
                    lastUpdatedBy: contact.lastUpdatedBy,
                    type: contact.type,
                    source: contact.source
                };

                data =
                    addNewLogEntry(
                        (new Date()),
                        `${globalContext.userName} marked as contacted`,
                        globalContext.userEmail,
                        101,
                        data)

                await ContactService.CreateOrUpdateAsync(accessToken, data);
                globalContext.setLoading(false);
                setForceRefresh(new Date());
            }
        }
    };

    const getColumns = (): IColumnDefinition<IContact, IContactFilters>[] => {
        return [
            {
                propName: "firstName",
                title: "First Name",
                customRenderer: (value, data) => <td className="align-top" key={`firstName${data.contactId}`}>
                    {value}
                </td>,
                sortable: true
            },
            {
                propName: "lastName",
                title: "Last Name",
                customRenderer: (value, data) => <td className="align-top" key={`lastName${data.contactId}`}>
                    {value}
                </td>,
                sortable: true
            },
            {
                propName: "contactId",
                title: "Actions",
                customRenderer: (value, data, allData) => {
                    return (
                        <td key={`align-top actions${value}`}>
                            {
                                data.email &&
                                <a
                                    className="me-2"
                                    href={`Mailto:${data.email}`}
                                    onClick={(e) => { e.stopPropagation(); }}>
                                    <FontAwesomeIcon icon={faEnvelope as IconProp} /></a>
                            }
                            {
                                data.phone &&
                                <a
                                    className="me-2"
                                    href={`Tel:${data.phone}`}
                                    onClick={(e) => { e.stopPropagation(); }}>
                                    <FontAwesomeIcon icon={faPhone as IconProp} /></a>
                            }
                            <span
                                className="quick-action pe-1"
                                title="Mark as contacted"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    markContactedAsync(data);
                                    return false;
                                }}><FontAwesomeIcon icon={faComments as IconProp} /></span>
                        </td>)
                },
                sortable: false
            },
            {
                propName: "email",
                title: "Email",
                customRenderer: (value, data) => <td className="align-top" key={`email${data.contactId}`}>
                    {value}
                </td>,
                sortable: true
            },
            {
                propName: "role",
                title: "Role",
                customRenderer: (value, data) => <td className="align-top" key={`role${data.contactId}`}>
                    {value}
                </td>,
                sortable: true
            },
            {
                propName: "type",
                title: "Type",
                customRenderer: (value, data) => <td className="align-top" key={`type${data.contactId}`}>
                    {value}
                </td>,
                sortable: true
            },
            {
                propName: "source",
                title: "Source",
                customRenderer: (value, data) => <td className="align-top" key={`type${data.contactId}`}>
                    {value}
                </td>,
                sortable: true
            },
            {
                propName: "created",
                title: "Created",
                customRenderer: (value, data) => <td className="align-top" key={`created${data.contactId}`}>
                    {formatDate(value)}
                </td>,
                sortable: true
            },
            {
                propName: "lastContactDate",
                title: "Last Contacted",
                customRenderer: (value, data) => <td className="align-top" key={`lastContacted${data.contactId}`}>
                    {formatDateTime(value)}
                </td>,
                sortable: true
            },
            {
                propName: "contactId",
                title: "Linked to",
                customRenderer: (value, data, allData) => {
                    let links = [];

                    if (data.fireDepartmentContactLinks &&
                        data.fireDepartmentContactLinks.length > 0) {
                        links.push("Department");
                    }

                    if (data.dealerContactLinks &&
                        data.dealerContactLinks.length > 0) {
                        links.push("Dealer");
                    }

                    return (
                        <td key={`align-top links${value}`}>
                            {
                                links.map((l, index) => <span>{l}{links.length > 1 && index < links.length ? ", " : ""}</span>)
                            }
                        </td>)
                },
                sortable: false
            },
        ];
    };

    const getTools = () => [
        <>
            {
                globalContext.roles.some(r => r === "Web.Admin") &&
                <Button
                    key="createnew"
                    variant="success"
                    className="full-sm"
                    onClick={() => { navigate(`/Contacts/Detail/`) }}>Create New</Button>
            }
        </>
    ];

    return (
        <CommonLookupDisplay<IContactFilters, IContact, IContactCreateOrUpdate>
            baseFilters={{
                sortBys: ["created"],
                sortDescending: true
            }}
            filterHints={{
                sortBys: "Array",
                sortDescending: "Boolean"
            }}
            dataTypeName="Contact"
            dataTypeNamePlural="Contacts"
            pathName="Contacts"
            primaryIdKey="contactId"
            className=""
            countriesFilterKey=""
            statesFilterKey=""
            filter={filter}
            forceReload={forceRefresh}
            setFilter={setFilter}
            columnDefinitions={getColumns()}
            service={ContactService}
            tools={getTools()}
        />
    );
}

export default ContactsLookup;
